/**
 * Helpers for make writing step type-specific config validation functions easy.
 */
import {DataFrameSpec} from 'data-frames/DataFrameTypes';
import findAndAssertColumn from 'data-frames/util/findAndAssertColumn';
import findDuplicates from 'util/findDuplicates';
import removeNulls from 'util/removeNulls';

export function coalesceMessages(...messages: Array<Array<string | null> | string | null>): Array<string> {
  return removeNulls(messages.flatMap((messages) => messages));
}

/** @package */
export function getDuplicateColumnsDisplayText(
  dataFrameSpec: DataFrameSpec,
  systemNames: Array<string>
): string | null {
  // Find all system names that are duplicated and convert them to display names
  const duplicateDisplayNames = findDuplicates(systemNames).map(
    (systemName) => findAndAssertColumn(dataFrameSpec, systemName).displayName
  );
  return duplicateDisplayNames.length === 0 ? null : duplicateDisplayNames.join(', ');
}

/** @package */
export function getDuplicateColumnsMessage(
  dataFrameSpec: DataFrameSpec,
  systemNames: Array<string>,
  messagePrefix: string,
  columnNamesPrefix: string = 'Duplicate columns: '
): string | null {
  const duplicatedColumnsDisplayText = getDuplicateColumnsDisplayText(dataFrameSpec, systemNames);
  return duplicatedColumnsDisplayText == null
    ? null
    : `${messagePrefix} ${columnNamesPrefix}${duplicatedColumnsDisplayText}`;
}

/** @package */
export function getColumnsDisplayText(
  dataFrameSpec: DataFrameSpec,
  systemNames: Array<string>
): string | null {
  const names = systemNames.map((systemName) => findAndAssertColumn(dataFrameSpec, systemName).displayName);
  return names.length === 0 ? null : names.join(', ');
}

/** @package */
export function getOverlappedColumnsMessage(
  dataFrameSpec: DataFrameSpec,
  systemNames1: Array<string>,
  systemNames2: Array<string>,
  messagePrefix: string
): string | null {
  const overlappedColumns = systemNames1.filter((systemName) => systemNames2.includes(systemName));
  return overlappedColumns.length === 0
    ? null
    : `${messagePrefix} Duplicate columns: ${getColumnsDisplayText(dataFrameSpec, overlappedColumns)}`;
}
